import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { getStudentAnalyticsSummary, getStudentAnalyticsMastery, getStudentAnalyticsHistory, getStudentAnalyticsDifficulties } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, AlertTriangle, BookOpen, Clock } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import { AnalyticsFilterBar } from '../components/AnalyticsFilterBar';
import { SummaryCards, AlertsCard } from '../components/student/StudentSummary';
import { StudentOverview } from '../components/student/StudentOverview';
import { MasteryTable, HistoryTable, DifficultiesTable } from '../components/student/StudentTables';

export default function StudentDashboard() {
  const { classId, studentId } = useParams();
  usePageTitle('Fiche élève');

  const [summary, setSummary] = useState(null);
  const [mastery, setMastery] = useState([]);
  const [history, setHistory] = useState([]);
  const [difficulties, setDifficulties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [searchParams] = useSearchParams();
  const loadedOnce = useRef(false);

  const load = useCallback(async (f = {}) => {
    setLoading(true);
    try {
      const [sumRes, masRes, histRes, diffRes] = await Promise.all([
        getStudentAnalyticsSummary(studentId, f),
        getStudentAnalyticsMastery(studentId, f),
        getStudentAnalyticsHistory(studentId, f),
        getStudentAnalyticsDifficulties(studentId, f),
      ]);
      setSummary(sumRes.data);
      setMastery(masRes.data.data);
      setHistory(histRes.data.data);
      setDifficulties(diffRes.data.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    }
    setLoading(false);
  }, [studentId]);

  useEffect(() => {
    if (loadedOnce.current) return;
    loadedOnce.current = true;
    const init = {};
    for (const k of ['date_from', 'date_to', 'subject_id', 'chapter_id', 'notion_id']) {
      if (searchParams.get(k)) init[k] = searchParams.get(k);
    }
    load(init);
  }, [load, searchParams]);

  const handleFiltersChange = (newFilters) => {
    load(newFilters);
  };

  if (!loading && !summary) {
    return (
      <div className="text-center py-24">
        <p className="text-muted-foreground">Élève introuvable ou aucune donnée.</p>
        <Link to={`/tracking/${classId}`}><Button variant="secondary" className="mt-4"><ArrowLeft className="w-4 h-4 mr-2" />Retour à la classe</Button></Link>
      </div>
    );
  }

  const cards = summary ? [
    { id: 'level', label: 'Niveau récent', value: summary.recent_level?.value, type: 'level', sub: summary.recent_level?.sufficient ? `${summary.recent_level.attempts_used} dernières tentatives` : summary.recent_level?.attempts_used ? `${summary.recent_level.attempts_used} tentative(s) disponible(s)` : null },
    { id: 'progress', label: 'Progression', value: summary.progress?.value, type: 'progress', sub: summary.progress?.sufficient ? `${summary.progress.previous_score}% → ${summary.progress.recent_score}%` : 'Données insuffisantes' },
    { id: 'mastered', label: 'Notions maîtrisées', value: summary.mastered_count, type: 'count', color: 'text-emerald-400' },
    { id: 'priority', label: 'Notions prioritaires', value: summary.priority_notions_count, type: 'count', color: summary.priority_notions_count > 0 ? 'text-amber-400' : 'text-muted-foreground' },
    { id: 'quizzes', label: 'QCM passés', value: summary.quiz_count, type: 'count', color: 'text-blue-400' },
  ] : [];

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble' },
    { id: 'mastery', label: `Notions (${mastery.length})`, icon: BookOpen },
    { id: 'history', label: `Historique (${history.length})`, icon: Clock },
    { id: 'difficulties', label: `Difficultés${difficulties.length > 0 ? ` (${difficulties.length})` : ''}`, icon: AlertTriangle },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="student-dashboard-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to={`/tracking/${classId}`}>
          <Button variant="ghost" size="sm" data-testid="back-to-class"><ArrowLeft className="w-4 h-4" /></Button>
        </Link>
        <div>
          <h1 className="text-3xl font-black tracking-tight" data-testid="student-name">{summary?.student_name || 'Chargement...'}</h1>
          {summary && <p className="text-muted-foreground text-sm mt-0.5">
            {summary.classe} · {summary.quiz_count} QCM · {summary.attempt_count} tentatives
            {summary.last_activity && <> · Dernière activité : {new Date(summary.last_activity).toLocaleDateString('fr-FR')}</>}
          </p>}
        </div>
      </div>

      {/* Filters */}
      <AnalyticsFilterBar onFiltersChange={handleFiltersChange} />

      {loading && (
        <div className="flex items-center justify-center py-12" data-testid="student-dashboard-loading">
          <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" /> <span className="text-muted-foreground text-sm">Chargement...</span>
        </div>
      )}

      {!loading && summary && (<>
      <SummaryCards cards={cards} />
      <AlertsCard alerts={summary.alerts} />

      {/* Tabs */}
      <div className="flex gap-1 bg-muted/30 p-1 rounded-lg w-fit flex-wrap" data-testid="student-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === t.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`student-tab-${t.id}`}>
            {t.icon && <t.icon className="w-4 h-4 inline mr-1.5" />}{t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <StudentOverview mastery={mastery} history={history} setTab={setTab} />}
      {tab === 'mastery' && <MasteryTable mastery={mastery} />}
      {tab === 'history' && <HistoryTable history={history} />}
      {tab === 'difficulties' && <DifficultiesTable difficulties={difficulties} />}
      </>)}
    </div>
  );
}
